import { useRef } from "react";
import { Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { ISSUE_ATTACHMENTS_FOLDER_NAME } from "@/domains/documents/services/folderService";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import * as pmRepo from "@/stores/pm/repository";
import { AttachMenuItems } from "./AttachMenuItems";

interface IssueAttachMenuProps {
  issueId: number;
  buildingId: string;
  buildingProjectId: number;
  /** Open the "Add from Franklin" document picker (owned by the caller). */
  onAddFromFranklin: () => void;
  /** Called once a freshly uploaded document has been linked to the issue. */
  onUploaded: () => void;
}

/**
 * IssueAttachMenu — a single compact dropdown that replaces the two bulky
 * "Add from Franklin" / "Upload" buttons in the expanded issue view
 * (WHA-3432). Hosts its own hidden file input + upload hook so the upload
 * path stays self-contained.
 */
export function IssueAttachMenu({
  issueId,
  buildingId,
  buildingProjectId,
  onAddFromFranklin,
  onUploaded,
}: IssueAttachMenuProps) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);

  const { handleFileUpload, isUploading } = useDocumentUpload({
    buildingId,
    projectId: String(buildingProjectId),
    requireProject: true,
    storagePathSegments: ["issues", String(issueId)],
    // Every file attached to an issue lands in the project's system
    // "Issue Attachments" folder, whichever control the user reached for.
    folderPathSegments: [ISSUE_ATTACHMENTS_FOLDER_NAME],
    onDocumentCreated: async (documentId) => {
      await pmRepo.addIssueDocument(issueId, documentId);
    },
    onUploadComplete: onUploaded,
  });

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // Sequential uploads keep the create_document_with_versioning RPC happy
    // when several files share the same target folder.
    for (const file of Array.from(files)) {
      await handleFileUpload(file);
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* WHA-3810 round 3: a quiet control, matching the panel's other
              secondary affordances (header actions, composer paperclip) — the
              outlined variant read as a primary-weight button next to the
              section label. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
            disabled={isUploading}
          >
            <Paperclip className="h-3.5 w-3.5" />
            {isUploading ? t.pm.issues.detail.uploading : t.pm.issues.detail.attach}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <AttachMenuItems
            onAddFromFranklin={onAddFromFranklin}
            onUpload={() => inputRef.current?.click()}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </>
  );
}
